import React from 'react';
import { useAppSelector } from '@state/store';
import { validateCfdSetup } from '../../validation/cfdValidation';

export interface ValidationPanelProps {
  onSelectSubroute?: (route: string) => void;
}

/**
 * ValidationPanel (Gate 1 CFD Setup Preflight)
 *
 * Verifies basic physical setup criteria:
 * - Wind speed and direction
 * - Computational domain extents
 * - Building geometry availability
 * - Physical boundary condition assignments
 *
 * Terminal passing status: "READY FOR ENGINE SPECIFICATION".
 * NEVER displays "Ready to run" in G1.
 */
export const ValidationPanel: React.FC<ValidationPanelProps> = () => {
  const activeScenarioId = useAppSelector((state) => state.study.study.activeScenarioId);
  const activeScenario = useAppSelector(
    (state) => state.study.study.scenarios[activeScenarioId]
  );
  const geometryCount = useAppSelector((state) => state.geometry.ids.length);

  const validation = validateCfdSetup(activeScenario?.cfdSetup, geometryCount);

  return (
    <div
      className="ssw-dock-panel"
      role="tabpanel"
      id="ssw-dock-panel-validation"
      aria-labelledby="ssw-dock-tab-validation"
    >
      <div className="ssw-dock-panel-header">
        <div className="ssw-dock-panel-title-group">
          <span className="ssw-dock-panel-kicker">PHYSICAL & NUMERICAL INTEGRITY</span>
          <h3 className="ssw-dock-panel-title">Validation</h3>
        </div>
        <div
          className={`ssw-dock-panel-badge ${validation.isReady ? 'is-ready' : 'is-warning'}`}
          style={{
            background: validation.isReady ? 'rgba(34, 197, 94, 0.12)' : 'rgba(234, 179, 8, 0.12)',
            color: validation.isReady ? '#15803d' : '#a16207',
            borderColor: validation.isReady ? 'rgba(34, 197, 94, 0.3)' : 'rgba(234, 179, 8, 0.3)',
            fontWeight: 600
          }}
        >
          {validation.statusLabel}
        </div>
      </div>

      <div className="ssw-dock-panel-body" style={{ overflowY: 'auto' }}>
        <div
          style={{
            border: '1px solid rgba(47, 120, 196, 0.18)',
            borderRadius: '4px',
            background: 'rgba(248, 246, 241, 0.90)',
            padding: '12px 14px'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px',
              paddingBottom: '6px',
              borderBottom: '1px solid rgba(47, 120, 196, 0.12)'
            }}
          >
            <span
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: '#2450A6'
              }}
            >
              CFD SETUP VALIDATION — SCENARIO: {activeScenario?.name.toUpperCase() || 'UNKNOWN'}
            </span>
            <span
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: '9.5px',
                color: '#64748b'
              }}
            >
              {validation.isReady
                ? '8/8 CRITERIA MET'
                : `${8 - validation.missingCount}/8 CRITERIA MET (${validation.missingCount} REQUIRED)`}
            </span>
          </div>

          {/* Validation Checklist Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '6px 16px',
              marginBottom: '12px'
            }}
          >
            {validation.checks.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  padding: '3px 6px',
                  borderRadius: '3px',
                  background: item.passed ? 'transparent' : 'rgba(234, 179, 8, 0.08)'
                }}
              >
                {item.passed ? (
                  <span
                    style={{
                      color: '#16a34a',
                      fontWeight: 700,
                      fontSize: '12px',
                      width: '14px',
                      textAlign: 'center'
                    }}
                    aria-label="Passed"
                  >
                    ✓
                  </span>
                ) : (
                  <span
                    style={{
                      color: '#d97706',
                      fontWeight: 700,
                      fontSize: '12px',
                      width: '14px',
                      textAlign: 'center'
                    }}
                    aria-label="Warning"
                  >
                    ⚠
                  </span>
                )}
                <span
                  style={{
                    color: item.passed ? '#334155' : '#b45309',
                    fontWeight: item.passed ? 400 : 600
                  }}
                >
                  {item.passed ? item.label : item.missingMessage}
                </span>
              </div>
            ))}
          </div>

          {/* Status & Engine Boundary Notice */}
          <div
            style={{
              padding: '8px 10px',
              borderRadius: '3px',
              background: validation.isReady ? 'rgba(34, 197, 94, 0.08)' : 'rgba(241, 245, 249, 0.8)',
              borderLeft: `3px solid ${validation.isReady ? '#16a34a' : '#94a3b8'}`,
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div>
              <div
                style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '9.5px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: validation.isReady ? '#15803d' : '#475569',
                  marginBottom: '2px'
                }}
              >
                STATUS: {validation.statusLabel}
              </div>
              <div style={{ fontSize: '10.5px', color: '#64748b' }}>
                {validation.isReady
                  ? 'Physical study bounds, wind vectors, and boundary conditions are explicitly defined. Proceed to the RUN phase to select an engine and snapshot an immutable Run Specification.'
                  : 'Complete the missing wind velocity, domain extents, or building geometry to fulfill preflight readiness.'}
              </div>
            </div>

            <div
              style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: '8.5px',
                color: '#2450A6',
                whiteSpace: 'nowrap',
                textAlign: 'right',
                fontWeight: 600
              }}
            >
              <span>GATE 2 RUN SPEC</span>
              <br />
              <span>ENGINES CONNECTED VIA ADAPTERS</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationPanel;
